import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

/*
 * Small portfolio snippets, drawn as die-cut rounded stickers: a poster, a type specimen,
 * an app screen. Used as chips around calls to action and inside product windows.
 */

type SnippetKind = 'poster' | 'type' | 'screen' | 'swatches'

const SnippetArt: FC<{ kind: SnippetKind }> = ({ kind }): ReactNode => {
    if (kind === 'poster') {
        return (
            <svg viewBox='0 0 100 120' aria-hidden='true' className='block h-auto w-full'>
                <rect width='100' height='120' fill='#c9dcf0' />
                <circle cx='38' cy='46' r='26' fill='#6f9bd1' />
                <circle cx='62' cy='58' r='20' fill='#282622' opacity='0.88' />
                <rect x='12' y='92' width='52' height='5' rx='2.5' fill='#282622' />
                <rect x='12' y='101' width='34' height='3' rx='1.5' fill='#282622' opacity='0.5' />
            </svg>
        )
    }

    if (kind === 'type') {
        return (
            <svg viewBox='0 0 100 120' aria-hidden='true' className='block h-auto w-full'>
                <rect width='100' height='120' fill='#f2ede5' />
                <text x='10' y='72' fontSize='58' fill='#282622' className='font-serif' letterSpacing='-2'>
                    Aa
                </text>
                <rect x='10' y='88' width='80' height='1' fill='#282622' opacity='0.25' />
                <text x='10' y='104' fontSize='9' fill='#282622' opacity='0.6' className='font-sans'>
                    Regular · Italic
                </text>
                <circle cx='84' cy='20' r='4' fill='#c9592f' />
            </svg>
        )
    }

    if (kind === 'screen') {
        return (
            <svg viewBox='0 0 100 120' aria-hidden='true' className='block h-auto w-full'>
                <rect width='100' height='120' fill='#e6e2d8' />
                <rect x='12' y='12' width='76' height='96' rx='8' fill='#fffdf8' />
                <rect x='18' y='20' width='64' height='36' rx='4' fill='#dce3d7' />
                <rect x='18' y='62' width='40' height='4' rx='2' fill='#282622' />
                <rect x='18' y='70' width='56' height='3' rx='1.5' fill='#282622' opacity='0.35' />
                <rect x='18' y='88' width='30' height='10' rx='5' fill='#282622' />
            </svg>
        )
    }

    return (
        <svg viewBox='0 0 100 120' aria-hidden='true' className='block h-auto w-full'>
            <rect width='100' height='120' fill='#fffdf8' />
            {['#282622', '#c9592f', '#6f9bd1', '#c9dcf0', '#e6e2d8'].map((color, index) => (
                <rect key={color} x='12' y={12 + index * 19} width='76' height='15' rx='3' fill={color} />
            ))}
        </svg>
    )
}

interface SnippetProps {
    kind: SnippetKind
    className?: string
}

/** A rounded die-cut sticker of a portfolio snippet. */
export const Snippet: FC<SnippetProps> = ({ kind, className }): ReactNode => {
    return (
        <div className={cn('sticker-shadow', className)}>
            <div className='rounded-[14px] bg-paper p-[7px]'>
                <div className='overflow-hidden rounded-[8px]'>
                    <SnippetArt kind={kind} />
                </div>
            </div>
        </div>
    )
}

/** The same snippet, but its bottom-right corner peels up under the pointer to show a plain paper backing. */
export const PeelSnippet: FC<SnippetProps> = ({ kind, className }): ReactNode => {
    return (
        <div className={cn('sticker-shadow', className)}>
            <div className='peel'>
                <div className='peel-face rounded-[14px] bg-paper p-[7px]'>
                    <div className='overflow-hidden rounded-[8px]'>
                        <SnippetArt kind={kind} />
                    </div>
                </div>
                <span aria-hidden='true' className='peel-flap' />
                <span aria-hidden='true' className='peel-hot pointer-events-auto absolute bottom-0 right-0 size-14 cursor-grab' />
            </div>
        </div>
    )
}
